import moment from "moment";

export interface PriceParams {
  price?: number;
  discount?: number;
  discountType?: number; // 1 = %, 2 = fixed
  tax?: number;
}

export interface StatusOption {
  value: string;
  name: string;
}


const helpers = {
  isValidEmail: (email: string): boolean => {
    email = helpers.cleanString(email);
    const regEmail =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}))$/;
    return regEmail.test(email);
  },

  isValidPhoneNo: (phone: string): boolean => {
    const regex = /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9]{10})+$/i;
    return phone ? regex.test(phone) : false;
  },

  isValidFormatForPassword: (password: string): string | null => {
    const uppercaseRegExp = /(?=.*?[A-Z])/;
    const lowercaseRegExp = /(?=.*?[a-z])/;
    const digitsRegExp = /(?=.*?[0-9])/;
    const specialCharRegExp = /(?=.*?[#?!@$%^&*-])/;
    const minLengthRegExp = /.{8,}/;

    if (password.length === 0) {
      return "Password is empty";
    }

    const isValid =
      uppercaseRegExp.test(password) &&
      lowercaseRegExp.test(password) &&
      digitsRegExp.test(password) &&
      specialCharRegExp.test(password) &&
      minLengthRegExp.test(password);

    return isValid
      ? null
      : "Password should contain at least one uppercase, one lowercase, one digit, one special character, and minimum 8 characters.";
  },

  isValidName: (name: string): boolean => {
    name = helpers.cleanString(name);
    const namePattern = /^[a-zA-Z\s-]+$/;
    return namePattern.test(name);
  },

  splitName: (name: string = ""): { firstName: string; lastName: string } => {
    const [firstName, ...lastName] = name.split(" ").filter(Boolean);
    return {
      firstName: firstName || "",
      lastName: lastName.join(" ") || "",
    };
  },
  
  isValidString: (text: string): boolean => {
    text = helpers.cleanString(text);
	//const textPattern = /^[a-zA-Z0-9 ]+$/;
	//const textPattern = /^[A-Za-z0-9\s.,!?'"()@&-]*$/;
	//const textPattern = /^[A-Za-z0-9\s.,!?'"()@&$\-\/\\]*$/;
	const textPattern = /^(?!.*[\\/]{2,})[A-Za-z0-9\s.,!?'"()@&\-$\/\\]*$/;
    return textPattern.test(text);
  },

  isValidWebsiteUrl: (url: string): boolean => {
	const pattern = new RegExp(
		'^' +                                     // Start of string
		'(https?:\\/\\/)?' +                      // Optional protocol
		'((([a-zA-Z0-9\\-]+\\.)+[a-zA-Z]{2,})|' + // Domain name
		'localhost|' +                            // OR localhost
		'\\d{1,3}(\\.\\d{1,3}){3})' +             // OR IP (v4) address
		'(\\:\\d+)?' +                            // Optional port
		'(\\/[-a-zA-Z0-9%_.~+]*)*' +              // Optional path
		'(\\?[;&a-zA-Z0-9%_.~+=-]*)?' +           // Optional query string
		'(\\#[-a-zA-Z0-9_]*)?' +                  // Optional fragment
		'$', 'i'                                  // End of string, case-insensitive
	);
	return pattern.test(url);
  },

  cleanString: (input: string): string => {
    return input.trim().replace(/\s+/g, " ");
  },

  calculateStepPercent: (total: number, current: number): string => {
    return `${(current / total) * 100}%`;
  },

  getFirstCharacter: (str: string): string => {
    if (!str) return "";
    return str.charAt(0).toUpperCase();
  },

  capitalizeFirstLetterOfEachWord: (str: string): string => {
    if (!str) return "";
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },

  splitAndCapitalizeFirstWord: (str: string, delimiter: string): string => {
    if (!str) return "";
    return str
      .split(delimiter)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },

  toTitleCase: (phrase: string): string => {
    if (!phrase) return "";
    return phrase
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },

  roleNameToString: (slug: string): string => {
    if (!slug) return "";
    //console.log('slug', slug);
    return slug
      .replace(/_/g, " ") // Replace all hyphens with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
  },

  getFileType: (url: string): string => {
    const extension = url.split(".").pop()?.toLowerCase() || "";
    switch (extension) {
      case "pdf":
        return "pdf";
      case "doc":
      case "docx":
        return "word";
      case "xls":
      case "xlsx":
        return "excel";
      case "mp3":
      case "ogg":
      case "wav":
        return "audio";
      case "mp4":
      case "mov":
        return "video";
      case "zip":
      case "7z":
      case "rar":
        return "archive";
      case "jpg":
      case "jpeg":
      case "png":
        return "image";
      default:
        return "alt";
    }
  },

  convertDateTime: (
    datetime: Date | null | undefined,
    new_format: string
  ): string => {
    if (datetime) {
      return moment(datetime).format(new_format);
    } else {
      return "";
    }
  },
  
  //Generate SKU for new product
  generateSKU: (): string => {
    //return "SKU-" + Math.random().toString(36).substring(2, 10).toUpperCase();
	 // If browser/node supports crypto.randomUUID()
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      const uuid = crypto.randomUUID().replace(/-/g, "");
      return "SKU-" + uuid.substring(0, 12).toUpperCase();
    }

    // Fallback method
    const uuid = "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx".replace(/[x]/g, () => {
      return ((Math.random() * 16) | 0).toString(16);
    });

    return "SKU-" + uuid.substring(0, 12).toUpperCase();
  },
  
  generateCouponCode: (length = 8): string => {
	  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no confusing chars
	  let code = "";

	  for (let i = 0; i < length; i++) {
		code += chars.charAt(Math.floor(Math.random() * chars.length));
	  }

	  return code;
  },
  
  // Global function: removes characters, keeps only valid integer/decimal value
  sanitizePrice: (value: string): string => {
	  if (!value) return "";
	 
	  // Remove everything except digits and dot
	  let clean = value.replace(/[^0-9.]/g, "");
	 
	  // Allow only ONE decimal point
	  const firstDot = clean.indexOf(".");
	  if (firstDot !== -1) {
		clean =
		  clean.slice(0, firstDot + 1) +
		  clean.slice(firstDot + 1).replace(/\./g, "");
	  }
	 
	  // Add 0 before decimal if user types ".5"
	  if (clean.startsWith(".")) {
		clean = "0" + clean;
	  }
	 
	  // Limit to max 2 decimal places (remove this block if unlimited decimals)
	  clean = clean.replace(/^(\d+\.\d{0,2}).*$/, "$1");
	 
	  return clean;
  },
  
  // src/utils/calculateSellingPrice.ts
  //calculate Selling Price
  calculateSellingPrice: ({
    price = 0,
    discount = 0,
    discountType = 0,
    tax = 0,
  }: PriceParams): number => {
    let finalPrice = Math.max(price, 0); // ensure non-negative base

    // ----- Apply Discount -----
    if (discount > 0) {
      if (discountType === 1) {
        finalPrice -= (finalPrice * discount) / 100; // percentage
      } else if (discountType === 2) {
        finalPrice -= discount; // fixed
      }
    }

    finalPrice = Math.max(finalPrice, 0); // prevent negative

    // ----- Apply Tax -----
    if (tax > 0) {
      finalPrice += (finalPrice * tax) / 100;
    }

    return Number(finalPrice.toFixed(2));
  },
  
  calculateDiscount: ({ price = 0, discount = 0, discountType = 0 }: PriceParams): number => {
	  const originalPrice = Math.max(price, 0);
	  let discountAmount = 0;

	  if (discount > 0) {
		if (discountType === 1) {
		  // percentage discount
		  discountAmount = (originalPrice * discount) / 100;
		} else if (discountType === 2) {
		  // flat discount
		  discountAmount = discount;
		}
	  }

	  // Ensure discount is never more than price
	  discountAmount = Math.min(discountAmount, originalPrice);

	  return discountAmount;
  },

  getInputOptions: (
    options: Record<number, string>,
    selectedOption: string
  ) => {
    // Remove the currently selected option (if valid)
    const filteredOptions: Record<number, string> = {};
    Object.entries(options).forEach(([key, value]) => {
      if (value !== selectedOption) {
        filteredOptions[Number(key)] = value;
      }
    });

    return filteredOptions;
  },
  
  toSwalOptions: (
	  options: StatusOption[]
	): Record<string, string> => {
	  return options.reduce<Record<string, string>>((acc, curr) => {
		if (curr.value !== "") {
		  acc[curr.value] = curr.name;
		}
		return acc;
	  }, {});
  },

  didOpen: () => {
    const content = document.querySelector("#root");
    if (content) (content as HTMLElement).style.overflow = "hidden";
  },

  didClose: () => {
    const content = document.querySelector("#root");
    if (content) (content as HTMLElement).style.overflow = "";
  },
  
  timeAgo: (created_at: Date | string): string => {
    return moment(created_at).fromNow(true);
  },
};

export default helpers;
